import MainLayout from "@/components/layout/admin/MainLayout.vue";
import Select2Multiple from "v-select2-multiple-component";
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import axios from "axios";
import { required } from "vuelidate/lib/validators";
import ExcelImportPreviewMixin from "@/mixins/excel-import-preview";
import ExcelImportConfirmErrorsMixin from "@/mixins/excel-import-confirm-errors";
import ValidationService from "@/services/ValidationService";

const maxSize5MB = (value) => !value || value.size <= 5 * 1024 * 1024;

export default {
  name: "StudentStatementImport",
  components: {
    MainLayout,
    Select2Multiple,
    Loading,
  },

  mixins: [ValidationService, ExcelImportPreviewMixin, ExcelImportConfirmErrorsMixin],

  data() {
    return {
      isLoading: false,
      disableImportBtn: false,
      disableConfirmBtn: false,
      disableFormFields: false,
      submitted: false,
      importStep: "form",
      periodArr: [],
      acd_year_id: null,
      file_name: "",
      selectedFile: null,
      invalidFileType: false,
      previewRows: [],
      previewTotal: 0,
    };
  },

  validations: {
    acd_year_id: { required },
    selectedFile: { required, maxSize5MB },
  },

  computed: {
    disabledTemplateBtn() {
      return this.disableFormFields || !this.acd_year_id;
    },

    isPreviewStep() {
      return this.importStep === "preview";
    },
  },

  methods: {
    getAcademicYear() {
      this.isLoading = true;
      axios.get("period/periodList")
        .then((res) => {
          this.periodArr = res.data.data || [];
        })
        .catch((error) => {
          this.handleError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    chooseFile() {
      if (this.isPreviewStep) {
        return;
      }
      this.clearSelectedFile();
      this.$refs.fileUploader.click();
    },

    onFileChange(event) {
      const file = event.target.files[0];
      this.clearSelectedFile();

      if (!file) {
        return;
      }

      const extension = file.name.split(".").pop()?.toLowerCase();
      if (!["xls", "xlsx"].includes(extension)) {
        this.invalidFileType = true;
        return;
      }

      this.selectedFile = file;
      this.file_name = file.name;
    },

    clearSelectedFile() {
      this.selectedFile = null;
      this.file_name = "";
      this.invalidFileType = false;
      if (this.$refs.fileUploader) {
        this.$refs.fileUploader.value = null;
      }
    },

    isChangedOrUnreadableFileError(error) {
      return !error?.response;
    },

    handleUnreadableSelectedFile() {
      window.alert("Invalid file. Please select the file again.");
      this.clearSelectedFile();
    },

    readSelectedFileForUpload() {
      const file = this.selectedFile;
      if (!file) {
        return Promise.reject(new Error("No file"));
      }

      const bytesToRead = Math.min(file.size || 1, 16);

      return file.slice(0, bytesToRead).arrayBuffer();
    },

    getTemplateFileName() {
      const year = this.periodArr.find((p) => String(p.id) === String(this.acd_year_id));
      const periodName = (year?.text || "year").replace(/\s+/g, "_");
      const now = new Date();
      const y = now.getFullYear();
      const m = String(now.getMonth() + 1).padStart(2, "0");
      const d = String(now.getDate()).padStart(2, "0");
      return `${periodName}_student_statements_${y}_${m}_${d}.xlsx`;
    },

    downloadTemplate() {
      if (this.disabledTemplateBtn) {
        this.submitted = true;
        this.$v.acd_year_id.$touch();
        return;
      }

      this.isLoading = true;
      axios.get("student/statement/excelTemplateDownload", {
        params: {
          acd_year_id: this.acd_year_id,
        },
        responseType: "blob",
      })
        .then(async (response) => {
          const contentType = response.headers["content-type"] || "";
          if (contentType.includes("application/json")) {
            const text = await response.data.text();
            const data = JSON.parse(text);
            this.$notification.error(
              data.message || "Unable to download template.",
              { timer: 5, position: "bottomCenter" }
            );
            return;
          }

          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = this.getTemplateFileName();
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch(async (error) => {
          await this.handleBlobDownloadError(error);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImport() {
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid || this.invalidFileType) {
        return;
      }
      this.validateImport();
    },

    validateImport() {
      this.isLoading = true;
      this.disableImportBtn = true;
      this.disableFormFields = true;

      this.readSelectedFileForUpload()
        .then(() => {
          const formData = new FormData();
          formData.append("acd_year_id", this.acd_year_id);
          formData.append("file", this.selectedFile);

          return axios.post("student/statement/excelImportValidate", formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        })
        .then((response) => {
          this.previewRows = this.clonePreviewRows(response.data.preview || []);
          this.previewTotal = response.data.total || this.previewRows.length;
          this.clearImportConfirmErrors();
          this.importStep = "preview";
        })
        .catch((error) => {
          if (this.isChangedOrUnreadableFileError(error)) {
            this.handleUnreadableSelectedFile();
          } else {
            this.handleImportValidationError(error);
          }
          this.disableFormFields = false;
          this.disableImportBtn = false;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    confirmImportSubmit() {
      this.clearImportConfirmErrors();

      if (!this.previewRows.length) {
        this.$notification.error("No preview data to import.", {
          timer: 3,
          position: "bottomCenter",
        });
        return;
      }

      this.isLoading = true;
      this.disableConfirmBtn = true;

      axios.post("student/statement/excelImportConfirm", {
        acd_year_id: this.acd_year_id,
        preview: this.previewRows,
      })
        .then((response) => {
          this.$notification.success(response.data.message || "Student statements imported successfully.", {
            timer: 3,
            position: "bottomCenter",
          });
          this.$router.push({ name: "studentStatementList" });
        })
        .catch((error) => {
          this.handleConfirmBackendError(error);
        })
        .finally(() => {
          this.isLoading = false;
          this.disableConfirmBtn = false;
        });
    },

    clonePreviewRows(rows) {
      return (rows || []).map((row) => ({ ...row }));
    },

    handleImportValidationError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data || {};
        const messages = data.errors?.length
          ? data.errors
          : [data.message || "Import validation failed."];

        window.alert(Array.isArray(messages) ? messages.join("\n") : String(messages));
        return;
      }

      this.handleError(error);
    },

    handleConfirmBackendError(error) {
      if (error.response?.status === 422) {
        const data = error.response.data || {};
        const messages = data.errors?.length
          ? data.errors
          : [data.message || data.msg || "Import confirmation failed."];
        const list = Array.isArray(messages) ? messages : [String(messages)];
        window.alert(list.join("\n"));
        return;
      }
      this.handleError(error);
    },

    getImportConfirmErrorOptions() {
      return {};
    },

    resetForm() {
      this.submitted = false;
      this.importStep = "form";
      this.disableFormFields = false;
      this.disableImportBtn = false;
      this.disableConfirmBtn = false;
      this.acd_year_id = null;
      this.clearSelectedFile();
      this.previewRows = [];
      this.previewTotal = 0;
      this.clearImportConfirmErrors();
      this.$v.$reset();
      this.getAcademicYear();
    },

    handleError(error) {
      if (error.response?.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
        return;
      }

      const message =
        error.response?.data?.message ||
        error.response?.data?.msg ||
        error.message ||
        "Something went wrong.";
      this.$notification.error(message, {
        timer: 5,
        position: "bottomCenter",
      });
    },

    async handleBlobDownloadError(error) {
      if (error.response?.status === 401) {
        this.$store.commit("logout");
        window.location.replace("login");
        return;
      }

      let message = "Unable to download template.";
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const parsed = JSON.parse(text);
          message = parsed.message || parsed.msg || message;
        } catch (_e) {
          // keep default message
        }
      } else if (data?.message || data?.msg) {
        message = data.message || data.msg;
      }

      this.$notification.error(message, {
        timer: 5,
        position: "bottomCenter",
      });
    },
  },

  mounted() {
    this.getAcademicYear();
  },
};
